import { client } from '@api/rest/client';
import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import {
  type WebScraperCompletedData,
  type WebScraperErrorData,
  WebScraperJobStatus,
  type WebScraperProgressData,
  type WebScraperProgressModalProps,
} from '@conversed/shared/src/central/dto/web-scraper.dto';
import { useWebScraperSocket } from '@hooks/web-scraper/useWebScraperSocket';
import webIcon from '@images/web-icon.svg';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const WebScraperProgressModal: React.FC<
  WebScraperProgressModalProps
> = ({ jobId, url, exclusions, requestError, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<WebScraperJobStatus>(
    requestError
      ? WebScraperJobStatus.Failed
      : jobId
        ? WebScraperJobStatus.Queued
        : WebScraperJobStatus.Initializing,
  );
  const [pagesFound, setPagesFound] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusMessage, setStatusMessage] = useState(
    status === WebScraperJobStatus.Initializing
      ? t('web-scraper.progress.starting-request')
      : status === WebScraperJobStatus.Queued
        ? t('web-scraper.progress.initializing')
        : t('web-scraper.progress.scraping-in-progress'),
  );
  const [error, setError] = useState<string | null>(requestError || null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pagesFound is intentionally, listed as a heartbeat — the effect re-runs on each new page event, resetting the stall timers.
  useEffect(() => {
    // Do not start stall timers once the job has reached a terminal state.
    if (
      status === WebScraperJobStatus.Completed ||
      status === WebScraperJobStatus.Failed
    ) {
      return;
    }

    // Warn after 5 seconds with no new progress.
    const scrapingTakingLongerTimer = setTimeout(() => {
      setStatusMessage(t('web-scraper.progress.scraping-taking longer'));
    }, 5 * 1000);

    // Fail after 60 seconds with no new progress.
    // Because pagesFound is a dependency, this timer resets on every new page event,
    // so mid-scrape stalls are caught in the same way as an initial unresponsive backend.
    const unresponsiveTimer = setTimeout(() => {
      setStatus(WebScraperJobStatus.Failed);
      setError(t('web-scraper.error.backend-unresponsive'));
    }, 60 * 1000);

    return () => {
      clearTimeout(scrapingTakingLongerTimer);
      clearTimeout(unresponsiveTimer);
    };
  }, [t, pagesFound, status]);

  useEffect(() => {
    if (requestError) {
      setStatus(WebScraperJobStatus.Failed);
      setError(requestError);
    }
  }, [requestError]);

  const handleProgress = useCallback(
    (data: WebScraperProgressData) => {
      if (data.jobId === jobId) {
        setStatus(data.status);
        setPagesFound(data.progress);
      }
    },
    [jobId],
  );

  const handleCompleted = useCallback(
    (data: WebScraperCompletedData) => {
      if (data.jobId === jobId) {
        setStatus(WebScraperJobStatus.Completed);
        setTotalPages(data.totalPages);
        onComplete();
      }
    },
    [jobId, onComplete],
  );

  const handleError = useCallback(
    (data: WebScraperErrorData) => {
      if (data.jobId === jobId) {
        console.error('Scraping error:', data);
        setStatus(WebScraperJobStatus.Failed);
        setError(data.error);
      }
    },
    [jobId],
  );

  const handleConnect = useCallback(() => {
    if (!jobId) {
      return;
    }

    client.webScraper
      .getJobStatus({ params: { jobId } })
      .then((response) => {
        const job = response.data;

        setStatus(job.status);
        setPagesFound(job.progress);

        if (job.total) {
          setTotalPages(job.total);
        }

        if (job.error) {
          setError(job.error);
        }
      })
      .catch((jobStatusError) => {
        // * A rejected fetch (e.g. a 404 for an already cleaned-up job) is
        // * not fatal - the modal keeps relying on live socket events.
        console.error(
          'Failed to resync web scraper job status:',
          jobStatusError,
        );
      });
  }, [jobId]);

  useWebScraperSocket(
    jobId ? handleProgress : undefined,
    jobId ? handleCompleted : undefined,
    jobId ? handleError : undefined,
    jobId ? handleConnect : undefined,
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const isInProgress =
    status === WebScraperJobStatus.Initializing ||
    status === WebScraperJobStatus.Queued ||
    status === WebScraperJobStatus.Scraping;

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        {/* Icon with status overlay */}
        <div className='flex justify-center mb-3'>
          <div className='relative'>
            <img src={webIcon} className='w-20 h-20' alt='web-scraper' />
            {status === WebScraperJobStatus.Completed && (
              <div className='flex items-center absolute top-0 bottom-auto right-[-5px] bg-green-500 rounded-full p-1 leading-none'>
                <MaterialIcon
                  icon='check'
                  classes={['text-white', 'text-lg', '!leading-none']}
                />
              </div>
            )}
            {status === WebScraperJobStatus.Failed && (
              <div className='flex items-center absolute top-0 bottom-auto right-[-5px] bg-red-500 rounded-full p-1 leading-none'>
                <MaterialIcon
                  icon='close'
                  classes={['text-white', 'text-lg', '!leading-none']}
                />
              </div>
            )}
          </div>
        </div>

        {/* URL */}
        <div className='flex gap-2 justify-center mb-2'>
          <p className='text-sm text-gray-600 text-center'>
            {t('web-scraper.progress.url')}:
          </p>
          <p className='text-sm font-medium text-gray-800 break-words text-center'>
            {url}
          </p>
        </div>

        {/* Excluded URLs — only shown when at least one exclusion was specified */}
        {exclusions && exclusions.length > 0 && (
          <div className='flex flex-col justify-center mb-4 min-w-0 overflow-hidden'>
            <p className='text-tiny text-gray-600 text-center whitespace-nowrap font-medium'>
              {t('web-scraper.progress.excluded-urls')}:
            </p>
            <div className='flex flex-col min-w-0 overflow-hidden'>
              {exclusions.map((exclusionUrl) => (
                <p
                  key={exclusionUrl}
                  title={exclusionUrl}
                  className='text-tiny font-normal text-gray-400 text-center truncate'>
                  {exclusionUrl}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Status content */}
        <div className='min-h-[150px] flex flex-col items-center justify-center'>
          {isInProgress && (
            <div className='text-center'>
              <p className='text-base font-medium text-primary-base mb-0'>
                {statusMessage}
              </p>

              {pagesFound > 0 && (
                <div className='mt-4'>
                  <div className='bg-gray-100 rounded-lg p-4 inline-block'>
                    <p className='text-4xl font-medium text-primary-base mb-1'>
                      {pagesFound}
                    </p>
                    <p className='text-xs text-gray-600'>
                      {t('web-scraper.progress.pages-discovered')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === WebScraperJobStatus.Completed && (
            <div className='text-center'>
              <p className='text-base font-medium text-primary-base mb-0'>
                {t('web-scraper.progress.completed')}
              </p>
              <p className='text-tiny text-gray-500 mb-3'>
                {t('web-scraper.progress.completed-message')}
              </p>

              <div className='bg-gray-100 rounded-lg p-4 inline-block'>
                <p className='text-4xl font-medium text-primary-base mb-1'>
                  {totalPages}
                </p>
                <p className='text-xs text-gray-600'>
                  {t('web-scraper.progress.pages-scraped')}
                </p>
              </div>
            </div>
          )}

          {status === WebScraperJobStatus.Failed && (
            <div className='text-center'>
              <p className='text-lg font-medium text-gray-800 mb-4'>
                {t('web-scraper.progress.failed')}
              </p>
              <p className='text-sm text-red-600 break-words px-4'>
                {error || t('web-scraper.progress.error-unknown')}
              </p>
            </div>
          )}
        </div>
      </div>

      {(status === WebScraperJobStatus.Completed ||
        status === WebScraperJobStatus.Failed) && (
        <div className='tailwind-modal-footer'>
          <ButtonT
            type='button'
            styling='primary'
            size='xs'
            rounding='md'
            onClick={handleClose}>
            {t('general.buttons.close')}
          </ButtonT>
        </div>
      )}
    </div>
  );
};
